/**
 * Shared constants for the asset list UI — icons, colors, metadata.
 */

import {
  Banknote,
  Receipt,
  FileKey,
  Building,
  Wrench,
  Package,
  Sparkles,
  HelpCircle,
  Brain,
  Search,
  FileSearch,
  CheckCircle2,
} from "lucide-react";
import type { BadgeColor } from "@/ui/primitives/badge";

// ---------------------------------------------------------------------------
// Asset type icons & colors
// ---------------------------------------------------------------------------

export const ASSET_TYPE_ICON: Record<string, typeof Banknote> = {
  "Cash at Bank": Banknote,
  "Accounts Receivable": Receipt,
  "Assets Subject to Lease": FileKey,
  "Interests in Land": Building,
  "Plant and Equipment": Wrench,
  "Stock / Inventory": Package,
  "Intangible Assets": Sparkles,
  "Contingent Assets": HelpCircle,
};

export const ASSET_TYPE_COLOR: Record<string, string> = {
  "Cash at Bank":
    "text-emerald-600 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-950/30 dark:border-emerald-800",
  "Accounts Receivable":
    "text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-950/30 dark:border-blue-800",
  "Assets Subject to Lease":
    "text-violet-600 bg-violet-50 border-violet-200 dark:text-violet-400 dark:bg-violet-950/30 dark:border-violet-800",
  "Interests in Land":
    "text-amber-600 bg-amber-50 border-amber-200 dark:text-amber-400 dark:bg-amber-950/30 dark:border-amber-800",
  "Plant and Equipment":
    "text-sky-600 bg-sky-50 border-sky-200 dark:text-sky-400 dark:bg-sky-950/30 dark:border-sky-800",
  "Stock / Inventory":
    "text-orange-600 bg-orange-50 border-orange-200 dark:text-orange-400 dark:bg-orange-950/30 dark:border-orange-800",
  "Intangible Assets":
    "text-pink-600 bg-pink-50 border-pink-200 dark:text-pink-400 dark:bg-pink-950/30 dark:border-pink-800",
  "Contingent Assets":
    "text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-950/30 dark:border-red-800",
};

export const ASSET_TYPE_LABEL: Record<string, string> = {
  "Cash at Bank": "Cash at Bank",
  "Accounts Receivable": "Accounts Receivable",
  "Assets Subject to Lease": "Subject to Lease",
  "Interests in Land": "Interests in Land",
  "Plant and Equipment": "Plant & Equipment",
  "Stock / Inventory": "Stock / Inventory",
  "Intangible Assets": "Intangible",
  "Contingent Assets": "Contingent",
};

export const ASSET_TYPE_BADGE_COLOR: Record<string, BadgeColor> = {
  "Cash at Bank": "emerald",
  "Accounts Receivable": "blue",
  "Assets Subject to Lease": "violet",
  "Interests in Land": "amber",
  "Plant and Equipment": "sky",
  "Stock / Inventory": "orange",
  "Intangible Assets": "pink",
  "Contingent Assets": "red",
};

// ---------------------------------------------------------------------------
// Filter tabs for the asset summary table
// ---------------------------------------------------------------------------

export const FILTER_TABS = [
  "All Assets",
  "Flagged",
  "Secured",
  "Unsecured",
  "Cash at Bank",
  "Accounts Receivable",
  "Plant & Equipment",
  "Excluded",
] as const;

export type FilterTab = (typeof FILTER_TABS)[number];

// ---------------------------------------------------------------------------
// Valuation source normalisation
// ---------------------------------------------------------------------------

/**
 * Canonicalises a raw valuation `sourceType` into the Title Case display key
 * used by the icon / reliability maps below.
 *
 * Agents write snake_case values (e.g. "director_estimate", "balance_sheet")
 * while practitioner-created valuations use Title Case ("Practitioner
 * Override"). Both forms must resolve to the same key, otherwise agent
 * valuations fall through to the "unknown" reliability tier and render as
 * "Unverified source" (the MOR bug this fixes). "ROCAP" is special-cased so
 * it stays fully uppercase regardless of the input casing.
 */
export function normalizeSourceType(sourceType: string): string {
  const trimmed = sourceType.trim();
  if (trimmed.toLowerCase() === "rocap") return "ROCAP";
  // Title-casing is a no-op for already-canonical values ("Practitioner
  // Override") but rescues lowercase agent output ("balance sheet") that
  // would otherwise miss the Title Case icon/reliability keys.
  return trimmed.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Valuation source icons (single character)
// ---------------------------------------------------------------------------

export const VALUATION_SOURCE_ICONS: Record<string, string> = {
  ROCAP: "R",
  "Balance Sheet": "B",
  "General Ledger": "G",
  "Asset Register": "A",
  "Inventory Report": "I",
  "Independent Valuation": "V",
  "Valuation Report": "V",
  "Director Estimate": "D",
  "Practitioner Override": "P",
  "Property Valuation": "V",
  "PPSR Extract": "S",
  "Bank Statement": "B",
};

/** Resolve the single-character icon for a valuation source (any casing). */
export function getValuationSourceIcon(sourceType: string): string {
  return VALUATION_SOURCE_ICONS[normalizeSourceType(sourceType)] ?? "?";
}

// ---------------------------------------------------------------------------
// Valuation reliability tiers (MOR — visual prioritisation of records)
// ---------------------------------------------------------------------------

/**
 * How much weight a practitioner can place on a valuation, driven by where the
 * figure came from. Lower `rank` = more reliable = shown first in the detail
 * panel. This is a fixed, built-in default (not yet org-configurable).
 */
export type ValuationReliabilityTier =
  "authoritative" | "high" | "medium" | "registry" | "low" | "unknown";

export interface ValuationReliabilityMeta {
  /** Sort rank — lower is more reliable (rendered first). */
  rank: number;
  /** Short label shown beneath the source type on each card. */
  label: string;
  /** One-line explanation for the legend / tooltip. */
  description: string;
  /** Coloured dot class (background). */
  dotClass: string;
  /** Text colour class for the tier label. */
  textClass: string;
  /** Coloured background for the source-icon chip. */
  iconClass: string;
  /** Badge colour for the legend. */
  badgeColor: BadgeColor;
}

export const VALUATION_RELIABILITY_META: Record<
  ValuationReliabilityTier,
  ValuationReliabilityMeta
> = {
  authoritative: {
    rank: 0,
    label: "Practitioner figure",
    description: "Entered or endorsed by the practitioner — authoritative.",
    dotClass: "bg-violet-500",
    textClass: "text-violet-600 dark:text-violet-400",
    iconClass:
      "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400",
    badgeColor: "violet",
  },
  high: {
    rank: 1,
    label: "Independent valuation",
    description:
      "Independent or market valuation — the most reliable external evidence.",
    dotClass: "bg-emerald-500",
    textClass: "text-emerald-600 dark:text-emerald-400",
    iconClass:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
    badgeColor: "emerald",
  },
  medium: {
    rank: 2,
    label: "Company records",
    description:
      "Company financial records (balance sheet, ledger, asset register).",
    dotClass: "bg-sky-500",
    textClass: "text-sky-600 dark:text-sky-400",
    iconClass: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-400",
    badgeColor: "sky",
  },
  registry: {
    rank: 3,
    label: "Registry extract",
    description: "Official registry extract (e.g. PPSR).",
    dotClass: "bg-teal-500",
    textClass: "text-teal-600 dark:text-teal-400",
    iconClass: "bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400",
    badgeColor: "teal",
  },
  low: {
    rank: 4,
    label: "Director-declared",
    description:
      "Director-declared or estimated figure (ROCAP, director estimate).",
    dotClass: "bg-amber-500",
    textClass: "text-amber-600 dark:text-amber-400",
    iconClass:
      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
    badgeColor: "amber",
  },
  unknown: {
    rank: 5,
    label: "Unverified source",
    description: "Source not recognised — treat with caution.",
    dotClass: "bg-gray-400",
    textClass: "text-muted-foreground",
    iconClass: "bg-muted text-muted-foreground",
    badgeColor: "gray",
  },
};

/** Maps each valuation `sourceType` to its reliability tier. */
export const VALUATION_SOURCE_RELIABILITY: Record<
  string,
  ValuationReliabilityTier
> = {
  "Practitioner Override": "authoritative",
  "Independent Valuation": "high",
  "Valuation Report": "high",
  "Property Valuation": "high",
  "Balance Sheet": "medium",
  "General Ledger": "medium",
  "Asset Register": "medium",
  "Inventory Report": "medium",
  "Bank Statement": "medium",
  "PPSR Extract": "registry",
  ROCAP: "low",
  "Director Estimate": "low",
};

/**
 * Resolve the reliability metadata for a given valuation source type.
 * Accepts either raw agent casing ("balance_sheet") or display casing
 * ("Balance Sheet") — both resolve to the same tier.
 */
export function getValuationReliability(
  sourceType: string,
): ValuationReliabilityMeta & { tier: ValuationReliabilityTier } {
  const tier =
    VALUATION_SOURCE_RELIABILITY[normalizeSourceType(sourceType)] ?? "unknown";
  return { tier, ...VALUATION_RELIABILITY_META[tier] };
}

// ---------------------------------------------------------------------------
// Validation flag labels
// ---------------------------------------------------------------------------

export const VALIDATION_FLAG_LABELS: Record<string, string> = {
  round_number: "Round Number",
  valuation_mismatch: "Valuation Mismatch",
  missing_documentation: "Missing Documentation",
  stale_data: "Stale Data",
  other: "Other",
};

// ---------------------------------------------------------------------------
// Task type icons (for agent-driven task loop)
// ---------------------------------------------------------------------------

export const TASK_TYPE_ICONS: Record<string, typeof Brain> = {
  survey: FileSearch,
  extract: Search,
  analyze: Brain,
  synthesize: Sparkles,
  verify: CheckCircle2,
};

export const TASK_STATUS_COLORS: Record<string, string> = {
  pending: "text-gray-500",
  dispatched: "text-sky-500",
  running: "text-blue-500",
  completed: "text-emerald-500",
  failed: "text-red-500",
};

// ---------------------------------------------------------------------------
// Sub-agent icon & color maps
// ---------------------------------------------------------------------------

export const SUB_AGENT_ICONS: Record<string, typeof Brain> = {
  asset_survey: FileSearch,
  asset_extraction: Search,
  asset_analysis: Brain,
  synthesizer: Brain,
};

export const SUB_AGENT_BORDER_COLORS: Record<string, string> = {
  asset_survey: "border-l-sky-500",
  asset_extraction: "border-l-amber-500",
  asset_analysis: "border-l-blue-500",
  synthesizer: "border-l-emerald-500",
};

export const SUB_AGENT_ICON_COLORS: Record<string, string> = {
  asset_survey: "text-sky-600 bg-sky-50 dark:text-sky-400 dark:bg-sky-950/30",
  asset_extraction:
    "text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-950/30",
  asset_analysis:
    "text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-950/30",
  synthesizer:
    "text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950/30",
};

// ---------------------------------------------------------------------------
// Timeline constants
// ---------------------------------------------------------------------------

export const MAX_SLOTS = 5;

export const MAIN_ICON_COLOR =
  "text-primary bg-primary/10 dark:text-primary dark:bg-primary/20";

export const SUB_AGENT_BADGE_COLOR: Record<string, BadgeColor> = {
  asset_survey: "sky",
  asset_extraction: "amber",
  asset_analysis: "blue",
  synthesizer: "emerald",
};
